
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface UseEstimateAnalysisProps {
  onAnalysisComplete?: (analysis: any) => void;
}

export function useEstimateAnalysis({ onAnalysisComplete }: UseEstimateAnalysisProps = {}) {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const [debugData, setDebugData] = useState<{
    requestData: any;
    responseData: any;
  } | null>(null);

  /**
   * Extract ADAS features from various possible locations in the data structure
   * This function now directly accepts the formatted features data already used in the UI
   */
  const extractAdasFeatures = (adasFeaturesInput: any): any => {
    if (!adasFeaturesInput) return null;
    
    console.log("Extracting ADAS features from input:", adasFeaturesInput);
    
    // If we're already passing the processed UI data structure, use it directly
    if (typeof adasFeaturesInput === 'object' && !Array.isArray(adasFeaturesInput)) {
      // Check if it looks like it's already organized with confidence values
      const firstKey = Object.keys(adasFeaturesInput)[0];
      if (firstKey && typeof adasFeaturesInput[firstKey] === 'object') {
        const firstItem = adasFeaturesInput[firstKey];
        if ('confidence' in firstItem || 'installed' in firstItem || 'maybeInstalled' in firstItem) {
          console.log("Using already formatted ADAS features data structure");
          return adasFeaturesInput;
        }
      }
    }
    
    // Case 1: Direct ADAS features object
    if (adasFeaturesInput.adas_features && typeof adasFeaturesInput.adas_features === 'object') {
      console.log("Found ADAS features directly in the input object");
      return adasFeaturesInput.adas_features;
    }
    
    // Case 2: ADAS features within vehicles array
    if (adasFeaturesInput.vehicles && adasFeaturesInput.vehicles.length > 0) {
      const vehicle = adasFeaturesInput.vehicles[0];
      if (vehicle.adas_features && typeof vehicle.adas_features === 'object') {
        console.log("Found ADAS features in vehicles[0].adas_features");
        return vehicle.adas_features;
      }
    }
    
    // Case 3: vehicleFeatures array with isAdasFeature flag
    if (adasFeaturesInput.vehicleFeatures && Array.isArray(adasFeaturesInput.vehicleFeatures)) {
      const adasFeatures = adasFeaturesInput.vehicleFeatures.filter(
        (feature: any) => feature.isAdasFeature
      );
      
      if (adasFeatures.length > 0) {
        console.log("Found ADAS features in vehicleFeatures array:", adasFeatures.length);
        // Convert array to object format for consistency
        const featuresObject = adasFeatures.reduce((acc: any, feature: any) => {
          acc[feature.featureNameNoBrand] = {
            name: feature.featureNameNoBrand,
            installed: feature.styles?.[0]?.asStandardCertain || false,
            maybeInstalled: feature.styles?.[0]?.asAvailable || false,
            location: feature.genericLocation || null
          };
          return acc;
        }, {});
        return featuresObject;
      }
    }
    
    // Case 4: If adasFeaturesInput is already formatted as expected
    if (typeof adasFeaturesInput === 'object' && Object.keys(adasFeaturesInput).length > 0) {
      // Check if it looks like an expected feature format
      const firstKey = Object.keys(adasFeaturesInput)[0];
      const firstValue = adasFeaturesInput[firstKey];
      
      if (typeof firstValue === 'object' && firstValue !== null && (
          'name' in firstValue || 
          'featureName' in firstValue || 
          'installed' in firstValue ||
          'asStandardCertain' in firstValue
        )) {
        console.log("Input appears to already be in ADAS features format");
        return adasFeaturesInput;
      }
    }
    
    // Case 5: Direct array of ADAS features
    if (Array.isArray(adasFeaturesInput)) {
      console.log("Found array of ADAS features, converting to object");
      const featuresObject = adasFeaturesInput.reduce((acc: any, feature: any) => {
        // Handle both object features and simple name/value features
        if (typeof feature === 'object') {
          const name = feature.featureNameNoBrand || feature.name || feature.featureName;
          if (name) {
            acc[name] = {
              name,
              installed: feature.installed || feature.confidence === 'Installed' || feature.asStandardCertain || false,
              maybeInstalled: feature.maybeInstalled || feature.confidence === 'Maybe Installed' || feature.asAvailable || false,
              location: feature.genericLocation || feature.location || null
            };
          }
        }
        return acc;
      }, {});
      return featuresObject;
    }
    
    // Case 6: Use the structure as is
    console.log("No recognized ADAS features structure, using as-is");
    return adasFeaturesInput;
  };

  const analyzeEstimate = async (estimateText: string, adasFeatures: any) => {
    if (!estimateText) {
      toast.error("No estimate text provided for analysis");
      return null;
    }

    setIsAnalyzing(true);
    
    try {
      // Improved logging for ADAS features
      console.log("Starting ADAS analysis with text length:", estimateText.length);
      console.log("ADAS features raw input:", adasFeatures);
      
      // Extract the ADAS features into a consistent format
      const processedAdasFeatures = extractAdasFeatures(adasFeatures);
      
      if (processedAdasFeatures) {
        console.log("ADAS features available:", Object.keys(processedAdasFeatures).length);
        console.log("ADAS features data:", processedAdasFeatures);
      } else {
        console.warn("No ADAS features provided or invalid format:", adasFeatures);
      }
      
      // Always use the persistent vector store ID
      const PERSISTENT_VECTOR_STORE_ID = "vs_67e6f0abcaf88191b784bed8d1c5be18";
      
      // Create request data object for debugging
      const requestData = {
        estimateText,
        adasFeatures: processedAdasFeatures,
        vectorStoreId: PERSISTENT_VECTOR_STORE_ID
      };
      
      // Update debug data with request
      setDebugData(prev => ({
        requestData,
        responseData: prev?.responseData || null
      }));
      
      const { data, error } = await supabase.functions.invoke('adas-analysis', {
        body: requestData
      });

      if (error) {
        console.error("Error calling ADAS analysis:", error);
        throw error;
      }

      console.log("Analysis result received:", data);
      
      // Update debug data with response
      setDebugData(prev => ({
        requestData: prev?.requestData || requestData,
        responseData: data.result
      }));
      
      if (!data.success) {
        throw new Error(data.error || "Failed to analyze estimate");
      }

      const result = data.result;
      setAnalysisResult(result);
      
      if (onAnalysisComplete) {
        onAnalysisComplete(result);
      }
      
      return result;
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to analyze estimate");
      return null;
    } finally {
      setIsAnalyzing(false);
    }
  };

  return {
    analyzeEstimate,
    isAnalyzing,
    analysisResult,
    debugData,
    clearAnalysis: () => {
      setAnalysisResult(null);
      setDebugData(null);
    }
  };
}
